import React, { useCallback, useEffect } from 'react'
import { MonitorData, QueueData, RelayerData } from '../../constants/DataModels'
import { useAppDispatch, useAppSelector } from '../../store/store'
import './RelayersPage.css'
import useTaskSubscriber from '../../hooks/useTaskSubscriber'
import { StatsState } from '../../store/stats/StatsTypes'
import { requestOverviewData } from '../../store/stats/StatsActions'
import ServerTable, { Column, RequestDataParams } from 'src/components/Tables/ServerTable'
import { getBridgeNameFromBridgeId } from 'src/constants/BlockchainConstants'
import { BlockchainWithIcon } from 'src/components/shared/BlockchainWithIcon'
import dayjs from 'dayjs'
import { formatUTCEpochTime } from 'src/constants/ConversionUtils'
import { fetchMonitors, fetchQueues, fetchRelayers, formatParamsForApi } from 'src/api/api'

export const MONITOR_COLUMNS: Column<MonitorData>[] = [
  {
    id: 'bridge_id',
    header: 'Bridge',
    canSort: true,
    cellRenderer: (row) => getBridgeNameFromBridgeId(row.bridge_id),
  },
  {
    id: 'blockchain',
    header: 'Blockchain',
    canSort: true,
    cellRenderer: (row) => BlockchainWithIcon({ blockchain: row.blockchain }),
  },
  {
    id: 'is_active',
    header: 'Active',
    canSort: true,
    cellRenderer: (row) => (row.is_active ? '✅' : '❌'),
  },
  {
    id: 'latest_height',
    header: 'Latest Block Height',
    canSort: true,
    cellRenderer: (row) => row.latest_height || '-',
  },
  {
    id: 'polled_height',
    header: 'Polled Tx Height',
    canSort: true,
    cellRenderer: (row) => row.polled_height || '-',
  },
  {
    id: 'blocks_left',
    header: 'Blocks Left',
    canSort: true,
    cellRenderer: (row) => row.blocks_left,
  },
  {
    id: 'last_saved_time',
    header: 'Last Polled Time',
    canSort: true,
    cellRenderer: (row) =>
      row.last_saved_time
        ? dayjs(row.last_saved_time * 1000).fromNow()
        : '-',
  },
  {
    id: 'total_events',
    header: 'Total Events',
    canSort: true,
    cellRenderer: (row) => row.total_events,
  },
  {
    id: 'total_unlinked',
    header: 'Unlinked Tx',
    canSort: true,
    cellRenderer: (row) => row.total_unlinked,
  },
  {
    id: 'total_in_transit_to_carbon',
    header: 'In Transit To Carbon',
    canSort: true,
    cellRenderer: (row) => row.total_in_transit_to_carbon,
  },
  {
    id: 'total_in_transit_from_carbon',
    header: 'In Transit From Carbon',
    canSort: true,
    cellRenderer: (row) => row.total_in_transit_from_carbon,
  },
]

export const RELAYER_COLUMNS: Column<RelayerData>[] = [
  {
    id: 'blockchain',
    header: 'Blockchain',
    cellRenderer: (row) => row.blockchain,
  },
  {
    id: 'sender',
    header: 'Address',
    cellRenderer: (row) => row.sender,
  },
  {
    id: 'events_count',
    header: 'No. of events',
    cellRenderer: (row) => row.events_count,
  },
  {
    id: 'most_recent_event',
    header: 'Last event',
    cellRenderer: (row) =>
      row.most_recent_event
        ? formatUTCEpochTime(parseInt(row.most_recent_event))
        : '-',
  },
]

export const QUEUE_COLUMNS: Column<QueueData>[] = [
  {
    id: 'name',
    header: 'Name',
    canSort: true,
    cellRenderer: (row) => row.name,
  },
  {
    id: 'waiting',
    header: 'Waiting',
    canSort: true,
    cellRenderer: (row) => row.waiting,
  },
  {
    id: 'delayed',
    header: 'Delayed',
    canSort: true,
    cellRenderer: (row) => row.delayed,
  },
  {
    id: 'active',
    header: 'Active',
    canSort: true,
    cellRenderer: (row) => row.active,
  },
  {
    id: 'failed',
    header: 'Failed',
    canSort: true,
    cellRenderer: (row) => row.failed,
  },
]

const RelayersPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const selector = useAppSelector

  // state from stats store
  const stats: StatsState = selector((state) => state.StatsReducer)
  const statsLoading: boolean = useTaskSubscriber('runStatsOverviewSaga')
  const overview = stats.overview
  const pendingUnlinked = overview.unlinked_count
  const inTransitFromCarbon = overview.in_transit_from_carbon_count
  const inTransitToCarbon = overview.in_transit_to_carbon_count

  const network = selector((state) => state.NetworkReducer.network)

  // get card data
  const handleOverviewRequest = () => {
    try {
      dispatch(requestOverviewData())
    } catch (e: any) {
      console.error(e)
    }
  }

  // initial render to set states
  useEffect(() => {
    handleOverviewRequest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network])

  // request functions
  const handleMonitorsRequest = useCallback(async (params: RequestDataParams) => {
    const response = await fetchMonitors(formatParamsForApi(params))
    return response.data
  }, [])
  const handleRelayersRequest = useCallback(async (params: RequestDataParams) => {
    const response = await fetchRelayers(formatParamsForApi(params))
    return response.data
  }, [])
  const handleQueuesRequest = useCallback(async (params: RequestDataParams) => {
    const response = await fetchQueues(formatParamsForApi(params))
    return response.data
  }, [])


  return (
    <div className='container main-wrapper'>
      <div className="row cards">
        <div className="dashboard-cards">
          <div className="dashboard-card">
            <h4 className="dashboard-card-title">{'Total unlinked'}</h4>
            <h1 className="dashboard-card-description">{statsLoading ? '0' : pendingUnlinked.toString()}</h1>
          </div>
          <div className="dashboard-card">
            <h4 className="dashboard-card-title">{'Total In-Transit Out'}</h4>
            <h1 className="dashboard-card-description">{statsLoading ? '0' : inTransitFromCarbon.toString()}</h1>
          </div>
          <div className="dashboard-card">
            <h4 className="dashboard-card-title">{'Total In-Transit In'}</h4>
            <h1 className="dashboard-card-description">{statsLoading ? '0' : inTransitToCarbon.toString()}</h1>
          </div>
        </div>
      </div>

      <div className="monitor-table">
        <ServerTable name="Monitors"
                     columns={MONITOR_COLUMNS}
                     onRequestData={handleMonitorsRequest}
                     filters={[]}
                     paramsPrefix={'m'}
        />
      </div>
      <div className="relayer_table">
        <ServerTable name="Relayers"
                     columns={RELAYER_COLUMNS}
                     onRequestData={handleRelayersRequest}
                     filters={[]}
                     paramsPrefix={'r'}
        />
      </div>
      <div className="queue-table">
        <ServerTable name="Queues"
                     columns={QUEUE_COLUMNS}
                     onRequestData={handleQueuesRequest}
                     filters={[]}
                     paramsPrefix={'q'}
        />
      </div>
    </div>
  )
}

export default RelayersPage
